import React from 'react';

import ConflictCardBase, { CONFLICT_VIEW_VARIANT } from './ConflictCardBase';
import ConflictOptionCard, { ResultTitleWithExpandToggle } from './ConflictOptionCard';
import { CHANGE_TYPE_LABEL, RESOLUTION_KEEP, RESOLUTION_OVERRIDE } from './constants';
import OrderList from './OrderList';
import { objectLabel, reorderResult } from './promotionsUtils';
import ReorderMoversSummary from './ReorderMoversSummary';
import type { ConflictChoice, PromotionChange, PromotionConflict } from './types';

/** One reorder conflict: the same collapsible card and two option cards as `ConflictCard`, for a
 * whole-collection decision. The body leads with the named-mover summary of live (drifted) order vs. the
 * order an override would write; each option card then lists the full order it would leave in place — so
 * unlike `ConflictCard` this never renders per-field `conflictDiffRows`/`formatValue` output. Shared
 * verbatim by approve and rollback, so the copy says "new order", not "promoted order": `after_value` is the
 * promoted order on approve but the restore (pre-apply) order on rollback. */
const ReorderConflictCard: React.FC<{
  change: PromotionChange;
  conflict: PromotionConflict;
  choice?: ConflictChoice;
  onResolveConflict?: (changeId: string, choice: ConflictChoice) => void;
}> = ({ change, conflict, choice, onResolveConflict }) => {
  // "Current" is the live target order (the conflict's drift); "new" is what an override would write — the
  // resolved order when the backend computed one, so a target-only member reads as kept, not deleted.
  const currentOrder = Array.isArray(conflict.actual_current) ? (conflict.actual_current as string[]) : [];
  const { order: newOrder, promotedKeys } = reorderResult(change);
  const keyLabels = change.key_labels ?? {};
  const label = objectLabel(change);
  const [orderListExpanded, setOrderListExpanded] = React.useState(false);

  const toggleOrderListExpanded = () => setOrderListExpanded((prev) => !prev);

  const keepResultTitle = (
    <ResultTitleWithExpandToggle
      expanded={orderListExpanded}
      onToggleExpanded={toggleOrderListExpanded}
      toggleLabel="both orders"
    >
      Result — keeps the current order
    </ResultTitleWithExpandToggle>
  );
  const overrideResultTitle = (
    <ResultTitleWithExpandToggle
      expanded={orderListExpanded}
      onToggleExpanded={toggleOrderListExpanded}
      toggleLabel="both orders"
    >
      Result — applies the new order
    </ResultTitleWithExpandToggle>
  );

  return (
    <ConflictCardBase
      title={label}
      chipLabel={CHANGE_TYPE_LABEL[change.change_type] ?? change.change_type}
      isResolved={choice !== undefined}
      configurationType={change.configuration_type}
      summary={<ReorderMoversSummary before={currentOrder} after={newOrder} keyLabels={keyLabels} />}
      conflictViewVariant={CONFLICT_VIEW_VARIANT.SIDE_BY_SIDE}
      conflictView={
        <>
          <ConflictOptionCard
            name={`resolution-${change.change_id}`}
            selected={choice === RESOLUTION_KEEP}
            title="Keep current"
            subtitle="the order on the target now"
            description="Keeps the order as it is on the target."
            resultTitle={keepResultTitle}
            onSelect={() => onResolveConflict?.(change.change_id, RESOLUTION_KEEP)}
          >
            <OrderList keys={currentOrder} keyLabels={keyLabels} expanded={orderListExpanded} />
          </ConflictOptionCard>
          <ConflictOptionCard
            name={`resolution-${change.change_id}`}
            selected={choice === RESOLUTION_OVERRIDE}
            title="Use incoming"
            subtitle="the new order"
            description="Replaces the target’s order with the new one."
            resultTitle={overrideResultTitle}
            onSelect={() => onResolveConflict?.(change.change_id, RESOLUTION_OVERRIDE)}
          >
            <OrderList
              keys={newOrder}
              keyLabels={keyLabels}
              expanded={orderListExpanded}
              before={currentOrder}
              promotedKeys={promotedKeys}
            />
          </ConflictOptionCard>
        </>
      }
    />
  );
};

export default ReorderConflictCard;
